import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider } from "@mui/material"
import IconButton from "@mui/material/IconButton"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import useHandleCloseWithDataSave from "@/screens/Companies/edit/components/locations/hooks/useHandleCloseWithDataSave"
import { LocationNotesForm } from "@/screens/Companies/edit/components/locations/LocationNotesForm"
import {
  type EditNotesDialogProps,
  type EditNotesQueryProps,
  EditNotesQuerySchema,
} from "@/screens/Companies/types/dialogProps"

export const EditNotesDialog: FC<EditNotesDialogProps> = ({ data, onClose, onSubmit, activeEditLocation }) => {
  const methods = useForm<EditNotesQueryProps>({
    values: { ...(activeEditLocation as EditNotesQueryProps), ...data },
    resolver: zodResolver(EditNotesQuerySchema),
    mode: "onBlur",
  })

  const handleSave = useCallback(
    async (value: EditNotesQueryProps) => {
      const isValid = await methods.trigger()
      if (isValid) {
        onSubmit(value)
        onClose({ data: { ...value }, reason: "success" })
      }
    },
    [onSubmit, onClose, methods]
  )

  const handleClose = useHandleCloseWithDataSave(methods.getValues, onClose)

  return (
    <Dialog open={true} onClose={handleClose} fullWidth>
      <DialogTitle className='pl-10'>
        <span className='font-normal text-primary'>Edit Location Notes</span>
      </DialogTitle>
      <IconButton aria-label='close' onClick={handleClose} className='absolute right-4 top-4 text-primary'>
        <CloseOutlined />
      </IconButton>
      <Divider />
      <DialogContent classes={{ root: "p-10" }}>
        <FormProvider {...methods}>
          <LocationNotesForm />
        </FormProvider>
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button variant='primary' appearance='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button variant='primary' onClick={methods.handleSubmit(handleSave)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
